import React, { useState, useEffect } from 'react';
import { User } from '../types/User';
import { Search, Filter, SortAsc, MessageCircle, UserMinus, Calendar, CheckCircle, X } from 'lucide-react';
import CustomNotification from './CustomNotification';
import { NetworkingService } from '../services/networkingService';
import { MeetingService } from '../services/meetingService';
import MeetingScheduleModal from './MeetingScheduleModal';
import { MessageService } from '../services/messageService';

interface MyConnectionsProps {
  currentUser: User;
  onSelectUser: (user: User) => void;
  connectionsData?: any[];
  connectionsLoaded?: boolean;
  meetingsData?: any[];
  meetingsLoaded?: boolean;
  onConnectionsUpdate?: (connections: any[]) => void;
  onMeetingsUpdate?: (meetings: any[]) => void;
}

interface ConnectionStatus {
  userId: string;
  hasMeeting: 'pending' | 'confirmed' | false;
  hasUnreadMessages: boolean;
  unreadCount: number;
}
// Mock status data - in real app this would come from API
const mockConnectionStatuses: ConnectionStatus[] = [
  { userId: '7', hasMeeting: false, hasUnreadMessages: false, unreadCount: 0 },
  { userId: '8', hasMeeting: false, hasUnreadMessages: false, unreadCount: 0 },
  { userId: '9', hasMeeting: 'pending', hasUnreadMessages: false, unreadCount: 0 }, // Has pending meeting
  { userId: '10', hasMeeting: 'confirmed', hasUnreadMessages: true, unreadCount: 2 }, // Has confirmed meeting and messages
];
const MyConnections: React.FC<MyConnectionsProps> = ({ 
  currentUser, 
  onSelectUser, 
  connectionsData, 
  connectionsLoaded, 
  meetingsData,
  meetingsLoaded,
  onConnectionsUpdate,
  onMeetingsUpdate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [connections, setConnections] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showSortModal, setShowSortModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'company' | 'position' | 'country' | 'recently-joined'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [companyFilter, setCompanyFilter] = useState('');
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState<User | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedUserForSchedule, setSelectedUserForSchedule] = useState<User | null>(null);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({ show: false, message: '', type: 'info' });
  const [conversations, setConversations] = useState<any[]>([]);
  const [userMeetings, setUserMeetings] = useState<any[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [animationPlayed, setAnimationPlayed] = useState(false);
  const [visibleConnections, setVisibleConnections] = useState<number>(0);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  useEffect(() => {
    // OPTIMIZATION: Use cached data from Dashboard if available
    if (connectionsData && connectionsLoaded && meetingsData && meetingsLoaded) {
      // Process connections data
      const connectedUsers = connectionsData.map(connection => {
        const otherUser = connection.requester_id === currentUser.id 
          ? connection.recipient 
          : connection.requester;
        
        return otherUser ? {
          ...otherUser,
          avatar: otherUser.avatar_url || otherUser.avatar
        } : null;
      }).filter(user => user) as User[];
      
      setConnections(connectedUsers);
      setUserMeetings(meetingsData);
      setDataLoaded(true);
      setLoading(false);
      
      // Play animation only once - on first load
      if (!animationPlayed) {
        setAnimationPlayed(true);
        setVisibleConnections(0);
        
        // Show connections one by one with delay
        const totalConnections = connectedUsers.length;
        const showNextConnection = (index: number) => {
          if (index < totalConnections) {
            setVisibleConnections(index + 1);
            setTimeout(() => showNextConnection(index + 1), 50); // 50ms delay between each
          }
        };
        
        setTimeout(() => showNextConnection(0), 100);
      } else {
        // If animation already played, show all connections immediately
        setVisibleConnections(connectedUsers.length);
      }
    }
    // REMOVED: Don't load data here - rely only on Dashboard cached data
  }, [currentUser.id, connectionsData, connectionsLoaded, meetingsData, meetingsLoaded]);

  // REMOVED: loadAllData - now relies only on Dashboard cached data

  const loadConversations = async () => {
    const convs = await MessageService.getUserConversations(currentUser.id);
    setConversations(convs);
  };

  const loadConversationsLazy = async () => {
    if (!conversationsLoaded && !conversationsLoading) {
      setConversationsLoading(true);
      try {
        const convs = await MessageService.getUserConversations(currentUser.id);
        setConversations(convs);
        setConversationsLoaded(true);
      } catch (error) {
        console.error('Error loading conversations:', error);
      }
      setConversationsLoading(false);
    }
  };

  const getUnreadCount = (userId: string): number => {
    // OPTIMIZATION: If conversations not loaded yet, return 0 (no badge shown)
    if (!conversationsLoaded) return 0;
    
    const conversation = conversations.find(conv => conv.partner?.id === userId);
    return conversation?.unreadCount || 0;
  };

  const hasBookedMeeting = (userId: string): boolean => {
    return userMeetings.some(meeting => 
      (meeting.requester_id === userId || meeting.recipient_id === userId) &&
      (meeting.status === 'pending' || meeting.status === 'confirmed')
    );
  };

  const getMeetingStatus = (userId: string): 'none' | 'pending' | 'confirmed' => {
    const meeting = userMeetings.find(meeting => 
      (meeting.requester_id === userId || meeting.recipient_id === userId) &&
      (meeting.status === 'pending' || meeting.status === 'confirmed')
    );
    
    if (!meeting) return 'none';
    return meeting.status as 'pending' | 'confirmed';
  };

  const filteredConnections = connections.filter(connection =>
    connection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    connection.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAndSortedConnections = React.useMemo(() => {
    let filtered = connections.filter(connection => {
      const matchesSearch = connection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        connection.company.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCompanyFilter = !companyFilter || 
        connection.company.toLowerCase().includes(companyFilter.toLowerCase());
      
      return matchesSearch && matchesCompanyFilter;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'company':
          aValue = a.company;
          bValue = b.company;
          break;
        case 'position':
          aValue = a.position;
          bValue = b.position;
          break;
        case 'country':
          aValue = a.country;
          bValue = b.country;
          break;
        case 'recently-joined':
          // For demo purposes, sort by name as we don't have join dates
          aValue = a.name;
          bValue = b.name;
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }

      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [connections, searchTerm, companyFilter, sortBy, sortDirection]);
  const handleRemoveConnection = (user: User) => {
    setUserToRemove(user);
    setShowRemoveModal(true);
  };

  const confirmRemoveConnection = async () => {
    if (userToRemove) {
      try {
        // Find the connection to remove
        const userConnections = await NetworkingService.getUserConnections(currentUser.id);
        const connectionToRemove = userConnections.find(conn => 
          (conn.requester_id === currentUser.id && conn.recipient?.id === userToRemove.id) ||
          (conn.recipient_id === currentUser.id && conn.requester?.id === userToRemove.id)
        );

        if (connectionToRemove) {
          // Update connection status to rejected (or you could delete it)
          const result = await NetworkingService.updateConnectionStatus(connectionToRemove.id, 'rejected');
          if (result.success) {
            setConnections(prev => prev.filter(conn => conn.id !== userToRemove.id));
            setNotification({
              show: true,
              message: `${userToRemove.name} has been removed from your connections`,
              type: 'success'
            });
          } else {
            setNotification({
              show: true,
              message: 'Failed to remove connection',
              type: 'error'
            });
          }
        }
      } catch (error) {
        console.error('Error removing connection:', error);
      }
      setShowRemoveModal(false);
      setUserToRemove(null);
    }
  };

  const cancelRemoveConnection = () => {
    setShowRemoveModal(false);
    setUserToRemove(null);
  };

  const handleScheduleMeeting = (user: User) => {
    const meetingStatus = getMeetingStatus(user.id);
    if (meetingStatus === 'pending') {
      setNotification({
        show: true,
        message: `Meeting confirmation pending with ${user.name}`,
        type: 'info'
      });
      return;
    } else if (meetingStatus === 'confirmed') {
      setNotification({
        show: true,
        message: `You have already booked a meeting with ${user.name}`,
        type: 'info'
      });
      return;
    }
    setSelectedUserForSchedule(user);
    setIsScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setSelectedUserForSchedule(null);
  };

  const handleClearAllFilters = () => {
    setCompanyFilter('');
    setSortBy('name');
    setSortDirection('asc');
  };

  const getConnectionStatus = (userId: string): ConnectionStatus => {
    return mockConnectionStatuses.find(status => status.userId === userId) || 
           { userId, hasMeeting: false, hasUnreadMessages: false, unreadCount: 0 };
  };

  // Show loading state immediately if data is not loaded yet
  if (!dataLoaded || loading) {
    return (
      <div className="p-4 lg:p-6 bg-gradient-to-br from-gray-50/50 to-white">
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600"></div>
            <div className="text-gray-500">Loading connections...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 bg-gradient-to-br from-gray-50/50 to-white">
      {/* Search and Filters */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search connections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-white shadow-md focus:outline-none focus:ring-1 focus:ring-yellow-600 transition-all duration-300 text-gray-600 placeholder-gray-400 text-sm lg:text-base placeholder:text-sm lg:placeholder:text-base"
          />
        </div>
        
        <div className="flex space-x-3 ml-4">
          <button 
            onClick={() => setShowFilterModal(true)}
            className="flex items-center space-x-2 px-4 py-3 border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-300 group"
          >
            <Filter className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" style={{ color: '#C89116' }} />
            <span className="font-medium">Filter</span>
          </button>
          <button 
            onClick={() => setShowSortModal(true)}
            className="flex items-center space-x-2 px-4 py-3 border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-300 group"
          >
            <SortAsc className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" style={{ color: '#C89116' }} />
            <span className="font-medium">Sort</span>
          </button>
        </div>
      </div>

      {/* Connections List */}
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredConnections.slice(0, visibleConnections).map((connection, index) => {
          const status = getConnectionStatus(connection.id);
          
          return (
          <div 
            key={connection.id} 
            className={`border-b border-gray-200 last:border-b-0 p-6 cursor-pointer transition-all duration-200 hover:bg-yellow-50 ${
              selectedUserId === connection.id ? 'bg-yellow-50 shadow-md' : ''
            }`}
            onClick={() => {
              setSelectedUserId(connection.id);
              onSelectUser(connection);
            }}
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center space-x-4 min-w-0">
                <div className="w-12 lg:w-16 h-12 lg:h-16 rounded-full flex items-center justify-center shadow-sm transition-all duration-300 relative text-gray-600 font-semibold text-lg bg-gray-200 flex-shrink-0">
                  {connection.avatar ? (
                    <img 
                      src={connection.avatar} 
                      alt={connection.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <span>
                      {connection.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  )}
                  {/* Online indicator - show for first user as example */}
                  {connection.id === '7' && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-lg lg:text-xl transition-colors duration-300 text-gray-800 break-words">
                    {connection.name}
                  </h3>
                  <p className="text-gray-600 font-medium text-sm">{connection.position}</p>
                  <p className="text-gray-500 text-sm font-medium italic">
                    {connection.company}
                  </p>
                  <p className="text-gray-400 text-sm flex items-center">{connection.country}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-center space-x-4 mt-3 lg:mt-0 lg:space-x-2">
                {/* Message button with unread indicator */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectUser(connection);
                    // OPTIMIZATION: Load conversations on demand when clicking message button
                    loadConversationsLazy().then(() => {
                    MessageService.markMessagesAsRead(currentUser.id, connection.id).then(() => {
                        loadConversations(); // Refresh after marking as read
                      });
                    });
                  }}
                  className="p-2 rounded-full transition-all duration-300 hover:shadow-lg bg-white hover:bg-gray-50 relative"
                  style={{ 
                    color: 'rgb(71 70 70)',
                    border: '1px solid rgb(255 255 255 / var(--tw-bg-opacity, 1))'
                  }}
                  title="Send Message"
                >
                  <MessageCircle style={{ width: '1.5rem', height: '1.5rem' }} />
                  {getUnreadCount(connection.id) > 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                      <span className="text-xs font-bold text-white">{getUnreadCount(connection.id)}</span>
                    </div>
                  )}
                </button>
                
                {/* Remove connection button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveConnection(connection);
                  }}
                  className="p-2 rounded-full transition-all duration-300 hover:shadow-lg bg-white hover:bg-white text-red-600"
                  style={{ 
                    border: '1px solid rgb(255 255 255 / var(--tw-bg-opacity, 1))'
                  }}
                  title="Remove Connection"
                >
                  <UserMinus style={{ width: '1.5rem', height: '1.5rem' }} />
                </button>
                
                {/* Calendar button with meeting indicator */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleScheduleMeeting(connection);
                  }}
                  className="p-2 rounded-full transition-all duration-300 hover:shadow-lg bg-white hover:bg-gray-50"
                  style={{ 
                    color: getMeetingStatus(connection.id) === 'confirmed' 
                      ? '#16a34a' 
                      : getMeetingStatus(connection.id) === 'pending'
                      ? '#eab308'
                      : 'rgb(71 70 70)',
                    border: '1px solid rgb(255 255 255 / var(--tw-bg-opacity, 1))'
                  }}
                  title={
                    getMeetingStatus(connection.id) === 'confirmed' 
                      ? "Meeting Booked" 
                      : getMeetingStatus(connection.id) === 'pending'
                      ? "Meeting Pending Confirmation"
                      : "Schedule Meeting"
                  }
                >
                  <Calendar style={{ width: '1.5rem', height: '1.5rem' }} />
                </button>
              </div>
            </div>
          </div>
          );
        })}
        </div>
      </div>

      {/* Meeting Schedule Modal */}
      {isScheduleModalOpen && selectedUserForSchedule && (
        <MeetingScheduleModal
          participant={selectedUserForSchedule}
          currentUser={currentUser}
          onClose={handleCloseScheduleModal}
        />
      )}

      {/* Remove Connection Modal */}
      {showRemoveModal && userToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <UserMinus className="w-6 h-6 text-red-600" />
                </div>
              </div>
              
              <h2 className="text-lg font-medium text-gray-800 text-center mb-6">
                Remove this connection?
              </h2>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-gray-600">
                      {userToRemove.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{userToRemove.name}</p>
                    <p className="text-sm text-gray-600">{userToRemove.position}</p>
                    <p className="text-sm text-gray-500">{userToRemove.company}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={cancelRemoveConnection}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRemoveConnection}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sort Modal */}
      {showSortModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-800">SORT</h2>
                <button
                  onClick={handleClearAllFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear All
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Sort contacts by</h3>
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as any)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600 appearance-none bg-white"
                    >
                      <option value="name">Name</option>
                      <option value="company">Company</option>
                      <option value="position">Position</option>
                      <option value="country">Country</option>
                      <option value="recently-joined">Recently joined</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Sort direction</h3>
                  <div className="relative">
                    <select
                      value={sortDirection}
                      onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600 appearance-none bg-white"
                    >
                      <option value="asc">Ascending</option>
                      <option value="desc">Descending</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowSortModal(false)}
                  className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                  style={{ backgroundColor: '#C89116' }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Custom Notification */}
      {notification.show && (
        <CustomNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ show: false, message: '', type: 'info' })}
        />
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-800">FILTERS</h2>
                <button
                  onClick={handleClearAllFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear All
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Company Name</h3>
                  <div className="relative">
                    <input
                      type="text"
                      value={companyFilter}
                      onChange={(e) => setCompanyFilter(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600"
                      placeholder="Filter by company name..."
                    />
                    {companyFilter && (
                      <button
                        onClick={() => setCompanyFilter('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                  style={{ backgroundColor: '#C89116' }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyConnections;